"use client";

import React from "react";
import Link from "next/link";

const ServicesAgreementStandard: React.FC = () => {
  return (
    <div className="terms py-3 py-md-4 py-xl-5 mb-5" id="standard_terms">
      <section className="container mb-5">
        <h2 className="mb-5 text-center">
          Services Agreement <span className="d-block text-primary">Standard Terms</span>
        </h2>
        <div className="row justify-content-center">
          {/* Table of contents */}
          <div className="col-md-10 col-lg-8 col-xxl-6 mb-5 mb-md-0">
            <ol className="list-unstyled terms-conditions-toc mb-0">
              <li className="bg-white p-2 rounded-3 shadow mb-3">
                <h3 className="lh-base lead fw-bold p-2 mb-0">
                  <i className="fas fa-chevron-right me-1" />
                  Standard Terms
                </h3>
              </li>
              <li className="bg-white p-2 rounded-3 shadow">
                <h3 className="lh-base lead fw-bold p-2 mb-0">
                  <Link href="#service_specific_terms">
                    <i className="fas fa-chevron-down me-1" />
                    Service-Specific Terms
                  </Link>
                </h3>
              </li>
            </ol>
          </div>
          {/* Terms */}
          <div className="col-md-14 col-lg-16 col-xxl-18 main terms-conditions-toc-content">
            <div className="mb-4">
              <h3 className="h6 fw-bold text-uppercase">Parties</h3>
              <ol className="mb-3">
                <li className="mb-1">Supplier as defined in the Order Form.</li>
                <li className="mb-1">Customer as defined in the Order Form.</li>
              </ol>

              <h3 className="h6 fw-bold text-uppercase">Background</h3>
              <ol className="mb-3" type="A">
                <li className="mb-1">Supplier is in the business of providing the Services.</li>
                <li className="mb-1">Customer wishes to receive and Supplier wishes to provide the Services on the terms set out in the Services Agreement.</li>
              </ol>

              <h3 className="h6 fw-bold text-uppercase">The Parties agree as follows:</h3>
              <ol className="decimal-list">
                <li id="interpretation">
                  <h4 className="lh-base lead fw-bold mb-2 text-uppercase">Interpretation</h4>
                  <ol className="decimal-list">
                    <li>
                      <h5 className="lh-base lead fw-bold mb-2">The following definitions and rules of interpretation
                        apply in the Services Agreement:</h5>
                      <p><strong>Applicable Data Protection Laws:</strong></p>
                      <ol className="decimal-list-alpha" type="a">
                        <li>To the extent the UK GDPR applies, the law of the United Kingdom or of a part of the United
                          Kingdom which relates to the protection of personal data.
                        </li>
                        <li>To the extent the EU GDPR applies, the law of the European Union or any member state of the
                          European Union to which Supplier is subject, which relates to the protection of personal data.
                        </li>
                      </ol>

                      <p><strong>Applicable Laws:</strong> all applicable laws, statutes, regulation and codes from time
                        to time in force in any relevant jurisdiction, including data protection laws other than the
                        Applicable Data Protection Laws, and applicable to the Parties in relation to the Services under
                        the Services Agreement (including without limitation export law and those governing the use of
                        networks, scanners, encryption devices, user monitoring and related software).</p>

                      <p><strong>Business Day:</strong> a day, other than a Saturday, Sunday or UK bank holiday.</p>

                      <p><strong>Business Hours:</strong> the period from 9.00 am to 5:30 pm GMT/BST on any Business Day
                        or as set forth in the Order Form.</p>

                      <p><strong>Commencement Date:</strong> the date of the last signature or as first set forth on the
                        Order Form and agreed by the parties as the effective date of the Services Agreement.</p>

                      <p><strong>Confidential Information:</strong> means any information whether supplied, made
                        available or otherwise accessed or accessible in any form, wholly or in part, and whether or not
                        marked confidential, by either party to the other under or in connection with the Services
                        Agreement and includes (but is not limited to) information relating to software and hardware
                        products, IT infrastructure, samples, equipment, drawings, specifications, information about a
                        party&lsquo;s clients and including customer characteristics and identities, staff and subcontractors
                        to a party including characteristics and identities, trade secrets, technical information and
                        know-how, performance or process data, cost and financial information, market opportunities,
                        business affairs, methods of doing business, strategic marketing, business plans and any
                        information, operation of digital platform, reports or analysis derived from the Confidential
                        Information, but does not information that is or becomes generally available to the public
                        otherwise than as a result of a breach of this agreement, is already available to a receiving
                        party on a non-confidential basis from a third party or is independently developed by a party
                        without relying on Confidential Information supplied by the other party.</p>

                      <p><strong>Customer:</strong> means the party referred to as Customer on the Order Form and any
                        persons, third party agents, subcontractors, consultants, employees and those acting on its
                        behalf.</p>

                      <p><strong>Customer&lsquo;s Equipment:</strong> any equipment, including tools, systems, cabling or
                        facilities, provided by Customer, its agents, employees, subcontractors or consultants which is
                        used directly or indirectly in relation to the supply of the Services including any such items
                        specified in the Order Form or Annex.</p>

                      <p><strong>Customer Materials:</strong> all documents, information, items and materials in any
                        form, whether owned by Customer or a third party, which are provided by Customer to Supplier in
                        connection with the Services, including the items provided pursuant to clause 5.6(d) or
                        otherwise specified in the Services Agreement.</p>

                      <p><strong>Customer Personal Data:</strong> any personal data which Supplier processes in
                        connection with the Services Agreement, in the capacity of a processor on behalf of Customer.
                      </p>

                      <p><strong>Customer&lsquo;s System:</strong> means the system, application and/or network set forth in
                        the Order Form or an Annex which Customer requires to be security tested.</p>

                      <p><strong>Defense.com Licence:</strong> means, where applicable as set forth in the Order Form, a
                        licence granted to Customer for access to and use of the Defense.com Platform and for the
                        provision of the Services and related Deliverables on and via the Defense.com Platform.</p>

                      <p><strong>Defense.com Platform:</strong> means Supplier&lsquo;s online security information and service
                        web portal and/or any other related Supplier facilities or systems to which Customer has been
                        granted access and use as set forth in the Order Form.</p>

                      <p><strong>Defense.com Users:</strong> means in respect of Defense.com Licence, the permitted
                        users who are designated by Customer to use Defense.com Platform.</p>

                      <p><strong>Deliverables:</strong> any output of the Services to be provided by Supplier to
                        Customer as specified in the Order Form or in the Services Agreement Service-specific Terms.</p>

                      <p><strong>EU GDPR:</strong> means the General Data Protection Regulation ((EU) 2016/679), as it
                        has effect in EU law.</p>

                      <p><strong>Fees:</strong> the monetary amounts due for the Services as set forth in the Order
                        Form.</p>

                      <p><strong>Good Industry Practice:</strong> means the exercise of that degree of skill, diligence
                        and foresight which would reasonably and ordinarily be expected from a skilled and experienced
                        service provider engaged in the provision of services similar to the Services under the same or
                        similar circumstances as those applicable to the Services Agreement and which are in accordance
                        with any codes of practice published by relevant trade associations.</p>

                      <p><strong>Initial Term:</strong> the first and minimum Services Agreement duration for any
                        Service as set forth in the Order Form.</p>

                      <p><strong>Intellectual Property Rights or IPRs:</strong> patents, utility models, rights to
                        inventions, copyright and neighbouring and related rights, moral rights, trade marks and service
                        marks, business names and domain names, rights in get-up and trade dress, goodwill and the right
                        to sue for passing off or unfair competition, rights in designs, rights in computer software,
                        data, database rights, rights to use, and protect the confidentiality of, confidential
                        information (including know-how and trade secrets) and all other intellectual property rights,
                        in each case whether registered or unregistered and including all applications and rights to
                        apply for and be granted, renewals or extensions of, and rights to claim priority from, such
                        rights and all similar or equivalent rights or forms of protection which subsist or will subsist
                        now or in the future in any part of the world.</p>

                      <p><strong>Milestone:</strong> a date by which a part or all the Services is to be completed, as
                        set forth in the Order Form.</p>

                      <p><strong>Monthly Recurring Service Fees:</strong> means any monthly recurring fees for the
                        applicable service payable by Customer as detailed on the Order Form.</p>

                      <p><strong>Order Form(s):</strong> shall mean the request on Supplier&lsquo;s standard Order Form from
                        Customer to Supplier for Services to be provided pursuant to the terms of the Services Agreement
                        which agreement, for the avoidance of doubt, applies in each case to a specific Order Form.</p>

                      <p><strong>Order Form Services Addendum:</strong> has the meaning given in clause 7.1.</p>

                      <p><strong>Professional Services:</strong> means consultant delivered Services, as defined by
                        Supplier including, but not limited to information security consultancy and penetration testing.
                      </p>
                    </li>
                  </ol>
                </li>
                <li id="structure_and_scope_of_the_services_agreement">
                  <h4 className="lh-base lead fw-bold mb-2 text-uppercase">Structure and scope of the Services
                    Agreement </h4>
                  <ol className="decimal-list">
                    <li>
                      <p>The Services Agreement creates a contractual framework between Supplier and Customer under
                        which: </p>
                      <ol type="a">
                        <li>Customer requests from Supplier to provide Services pursuant to the terms of the Services
                          Agreement; and
                        </li>
                        <li>Supplier agrees to provide the Services pursuant to the terms of the Services Agreement.
                        </li>
                      </ol>
                    </li>
                    <li>Each Service specified will be set forth in the Order Form and any applicable Annex.</li>
                    <li>
                      <p>In the event of any conflict or ambiguity, except where otherwise provided, the order of
                        precedence for the Services Agreement shall be as follows: </p>
                      <ol type="a">
                        <li>the applicable Order Form;</li>
                        <li>the applicable Annex or Order Form Services Addendum; and</li>
                        <li>the body of these Services Agreement Standard Terms or the Services Agreement
                          Service-specific Terms.
                        </li>
                      </ol>
                    </li>
                    <li>The Customer and Supplier may agree to one or more Order Forms, each forming a separate Services
                      Agreement, for the provision of Services.
                    </li>
                    <li>For the Services Agreement to be valid and effective, the Order Form must be confirmed in
                      writing and signed by an authorised representative of each party. Upon signature by both
                      authorised representatives, the Services Agreement shall be binding (and incapable of cancellation
                      other than through the termination provisions contained in clause 13 below) and the Fees and any
                      other charges shall become due as set forth in the Order Form in accordance with the Services
                      Agreement.
                    </li>
                    <li>Each Party warrants to the other Party that it (1) has the full capacity and authority to enter
                      into and perform the Services Agreement and that the Services Agreement is executed by a duly
                      authorised representatives; (2) is the owner, or has the relevant consent from the owner, of all
                      Systems, applications, networks, premises and any other asset that is set out in the Order Form;
                      and (3) will comply with all Applicable Laws.
                    </li>
                  </ol>
                </li>
                <li id="commencement_and_duration">
                  <h4 className="lh-base lead fw-bold mb-2 text-uppercase">Commencement and duration</h4>
                  <ol className="decimal-list">
                    <li>The Services Agreement shall commence on the Commencement Date and shall continue for the
                      Initial Term unless terminated earlier in accordance with clause 13 (Termination). Following the
                      Initial Term of the Services Agreement, where the Services are of the type that can continue and
                      are not specified as not continuing in the Order Form, the Services Agreement shall extend
                      automatically for additional terms of the same duration as the Initial Term (each
                      an &ldquo;Extension Term&rdquo;) or as otherwise set forth in the Order Form unless terminated on
                      at least 60 days&rsquo; prior written notice by either party or as otherwise set forth in the
                      Order Form.
                    </li>
                    <li>If there are no incomplete Services under the Order Form as of the date notice to terminate is
                      served under clause 3.1, such notice shall terminate the Services Agreement on the expiry of the
                      notice period and not later than the expiration of the Initial Term or Extension Term, as
                      applicable. Except where provided otherwise or by agreement of the parties, any incomplete Service
                      under the Order Form shall be completed and/or paid for (where completion is not possible due to
                      Customer&rsquo;s failure to perform under the Services Agreement) prior to any termination of the
                      Services Agreement.
                    </li>
                    <li>Customer may procure any of the Services by executing the agreed Order Form with Supplier.</li>
                    <li>Supplier shall provide the Services from the Commencement Date or other date specified in the
                      Order Form.
                    </li>
                  </ol>
                </li>
                <li id="provision_of_services">
                  <h4 className="lh-base lead fw-bold mb-2 text-uppercase">Provision of Services</h4>
                  <ol className="decimal-list">
                    <li>
                      <p>Supplier will provide, and Customer will receive and have use of the Services and any related
                        Deliverables (where applicable, by grant of a Defense.com Licence) in accordance with the
                        Services Agreement for the Initial or Extension Term, as applicable, set out in the Order Form
                        whereby:</p>
                      <ol type="a">
                        <li>each Service and/or Deliverable specified to be provided will be provided in accordance with
                          the Order Form and any applicable Annex; and
                        </li>
                        <li>Supplier will provide, deliver or otherwise make available such Service and/or Deliverables
                          with Good Industry Practice skill and care, in a timely manner and in accordance with the
                          other provisions of the Services Agreement.
                        </li>
                        <li>Supplier shall, where it deems appropriate, appoint a contact person in respect of the
                          Services to be performed, such person shall be designated before the delivery of a relevant
                          Service.
                        </li>
                        <li>Where applicable, Supplier shall observe all health and safety and security requirements
                          that apply at any of Customer&lsquo;s premises and that have been communicated to Supplier under
                          clause 5.6(e), provided that Supplier shall not be liable under the Services Agreement if, as
                          a result of such observation, it is in breach of any of its obligations under the Services
                          Agreement.
                        </li>
                        <li>Supplier may use a subcontractor, contracted under the Services Agreement terms, to assist
                          with delivery of Services and will carry out the appropriate due diligence to ensure any such
                          subcontractor has the required qualifications and experience to deliver the Services.
                        </li>
                      </ol>
                    </li>
                  </ol>
                </li>
                <li id="use_of_the_services">
                  <h4 className="lh-base lead fw-bold mb-2 text-uppercase">Use of the Service(s)</h4>
                  <ol className="decimal-list">
                    <li>
                      <p>Customer will:</p>
                      <ol type="a">
                        <li>provide to Supplier all necessary co-operation in relation to the Services Agreement
                          including the Order Form and any applicable Annex; and all necessary access to such
                          information as may be required by Supplier to provide the Services including, but not limited
                          to, relevant Customer staff and/or agents, customer data, security access information and
                          configurations services;
                        </li>
                        <li>carry out all customer obligations in a timely and efficient manner;</li>
                        <li>ensure that Customer&rsquo;s Equipment including network and systems comply with the
                          relevant specification and use restrictions provided by Supplier from time to time and comply
                          with any security, information security and technical procedures and requirements in relation
                          to the Services and/or any Deliverables.
                        </li>
                      </ol>
                    </li>
                    <li>Customer is responsible for having and maintaining an adequate Customer environment and
                      uninterrupted internet connectivity to receive and/or enable the use of the Services and/or
                      Deliverables. Supplier shall not be liable for any incompatibility, failure, use or misuse by
                      Customer related to Customer&rsquo;s environment.
                    </li>
                    <li>
                      <p>Customer shall not: </p>
                      <ol type="a">
                        <li>infringe any Intellectual Property Rights that belong to or are licensed to Supplier;</li>
                        <li>create, upload, download, access, store, into the Services and/or any Deliverable any
                          malicious code, programs, viruses, malware or other types of malicious software or material,
                          or links to such software, that are unlawful, insider or confidential information,
                          advertisements or solicitation for any products or services, or could disrupt or harm the
                          operation of such Service and/or Deliverables or incite another to do so; or
                        </li>
                        <li>copy, reverse engineer, decompile, disassemble or modify a Service and/or any Deliverable or
                          any part, feature, function or user interface thereof, or otherwise reduce to
                          human-perceivable form all or any part of Service and/or any Deliverable, or use or attempt to
                          use any automated program to access any Service and/or any Deliverable, or to search, display,
                          or obtain links to any part of a Service and/or any Deliverable.
                        </li>
                      </ol>
                    </li>
                    <li>Customer agrees to indemnify Supplier from any losses suffered, or liabilities incurred because
                      of Customer&rsquo;s breach of clause 5.3.
                    </li>
                    <li>
                      <p>Customer shall not: </p>
                      <ol type="a">
                        <li>knowingly withhold information which may affect Supplier&rsquo;s ability to provide any of
                          the Services and/or Deliverables to Customer or to others (including, where applicable,
                          Defense.com Users), or security or integrity of any of the Services and/or Deliverables;
                        </li>
                        <li>use any Service and/or Deliverable to impersonate any person, or to misrepresent
                          Customer&rsquo;s or any Platform User&rsquo;s identity;
                        </li>
                        <li>engage in sending unsolicited messages to any number or users or via the internet by using
                          any Service and/or Deliverable;
                        </li>
                        <li>use the Service and/or Deliverables in a way which in Supplier reasonable opinion is not
                          within the intended use of such Service or Deliverable;
                        </li>
                        <li>engage in abusive or excessive usage of any Service and/or Deliverable which is usage
                          significantly in excess of average usage patterns, as determined by Supplier, that adversely
                          affects the speed, responsiveness, stability, availability or functionality of any Service
                          and/or Deliverable for other users;
                        </li>
                        <li>make any Service and/or Deliverable available to, or use any Service and/or Deliverable for
                          the benefit of, anyone other than Customer, unless and to the extent expressly stated
                          otherwise in the Order Form;
                        </li>
                        <li>lend, sell, resell, license, sublicense, distribute, make available, rent or lease any
                          Service and/or Deliverable, or include any Service and/or Deliverable in a service or
                          outsourcing offering, unless otherwise agreed in writing with Supplier;
                        </li>
                        <li>access any Service and/or Deliverable to build a competitive solution or service or to
                          benchmark with a non-Supplier service; or
                        </li>
                        <li>use any Service and/or Deliverable in Customer&rsquo;s own products or services,
                          commercially exploit or otherwise make any Service and/or Deliverable available to any third
                          party in any way, unless expressly consented to by Supplier.
                        </li>
                      </ol>
                    </li>
                    <li>
                      <p>Customer shall: </p>
                      <ol type="a">
                        <li>co-operate with Supplier as reasonably requested in all matters relating to the Services;
                        </li>
                        <li>assign a contact person in respect of the Services to be performed under the Order Form, as
                          identified in the Order Form;
                        </li>
                        <li>provide, for Supplier, its agents, subcontractors, consultants and employees, in a timely
                          manner and at no charge, access to Customer&lsquo;s premises, office accommodation, data and other
                          facilities as reasonably required by Supplier to carry out the Services, including any such
                          access as is specified in the Order Form;
                        </li>
                        <li>provide to Supplier in a timely manner all documents, information, items and materials in
                          any form (whether owned by Customer or a third party) and meeting attendance by the assigned
                          contact person, project manager and/or any key staff as set forth in the Order Form or
                          otherwise reasonably requested by Supplier in connection with the Services and ensure that
                          they are accurate and complete;
                        </li>
                        <li>inform Supplier in writing of all health and safety and security requirements that apply at
                          any of Customer&lsquo;s premises;
                        </li>
                        <li>ensure that all Customer&lsquo;s Equipment is in good working order and suitable for the purposes
                          for which it is used in relation to the Services and conforms to all relevant Applicable Law
                          requirements or standards;
                        </li>
                        <li>obtain and maintain all necessary licences and consents in accordance with relevant
                          Applicable Law and comply with all relevant legislation as required to enable Supplier to
                          provide the Services;
                        </li>
                        <li>at the request of Supplier, agree to a service review with Supplier once every 6 months or
                          as otherwise reasonably requested; and
                        </li>
                        <li>where applicable as designated in the Order Form, in respect of each Defense.com Licence
                          granted, appoint Defense.com Users, to the maximum number of users as stated in the Order
                          Form, who shall be the only users permitted to access the Defense.com Platform and be provided
                          with the Services and/or Deliverables.
                        </li>
                      </ol>
                    </li>
                    <li>If Supplier&lsquo;s performance of its obligations under the Services Agreement is prevented or
                      delayed by any act or omission of Customer, its agents, subcontractors, consultants or employees
                      or any other third-party supplier then, without prejudice to any other right or remedy it may
                      have, Supplier shall be allowed an extension of time to perform its obligations equal to the delay
                      caused by Customer or other third-party supplier or for as long as Supplier deems at its
                      discretion the prevention or delay necessitates.
                    </li>
                    <li>Both Parties shall maintain business continuity and disaster recovery plans to ensure the
                      continuity of the Services in the event of an unforeseen interruption and any other prudent
                      procedures and measures that are reasonably necessary to prevent the disruption of the Services.
                      Customer shall, in the event of an unforeseen interruption, use best efforts to cooperate with
                      Supplier to ensure the uninterrupted provision of Services.
                    </li>
                  </ol>
                </li>
                <li id="non_solicitation_and_employment">
                  <h4 className="lh-base lead fw-bold mb-2 text-uppercase">Non-solicitation and employment</h4>
                  <ol className="decimal-list">
                    <li>Each party shall not, without the prior express written consent of the other party, at any time
                      until the expiry of 24 months after the completion of such Services, solicit or entice away from
                      the other party or directly attempt to employ any person who is, or has been, engaged as an
                      employee, consultant or subcontractor of the other party.
                    </li>
                  </ol>
                </li>
                <li id="order_form_services_addendum">
                  <h4 className="lh-base lead fw-bold mb-2 text-uppercase">Order Form Services Addendum</h4>
                  <ol className="decimal-list">
                    <li>Either party may propose changes to the scope or execution of the Services but no proposed
                      changes shall come into effect until a relevant Order Form Services Addendum has been formally
                      agreed by both parties. The <strong>Order Form Services Addendum</strong> shall be a document (or
                      email where permitted by Supplier at its sole discretion) citing to the Order Form and setting out
                      the proposed changes and the effect that those changes will have on the Service(s), Fees, any
                      timetable and/or any other Order Form terms.
                    </li>
                    <li>If Supplier wishes to make a material change to the Services provided to Customer it shall
                      provide a draft Order Form Services Addendum to Customer.
                    </li>
                    <li>If Customer wishes to make a change to the Services it shall notify Supplier and provide as much
                      detail as Supplier reasonably requires of the proposed changes, including the timing of the
                      proposed change; and Supplier shall, as soon as reasonably practicable after receiving the
                      information, provide a draft Order Form Services Addendum to Customer.
                    </li>
                    <li>If the parties agree to the Order Form Services Addendum, they shall sign it and that Order Form
                      Services Addendum shall amend the relevant Order Form. If the parties are unable to agree the
                      Order Form Services Addendum, either party may request termination of the affected Service, such
                      termination to take effect as expressly agreed by the parties; however, termination of a Service
                      under this clause shall not affect Customer&rsquo;s payment obligations (as of the date of any
                      such Service termination) under the Services Agreement.
                    </li>
                  </ol>
                </li>
                <li id="fees_other_charges_and_payment">
                  <h4 className="lh-base lead fw-bold mb-2 text-uppercase">Fees, other charges and payment</h4>
                  <ol className="decimal-list">
                    <li>In consideration of the administration and allocation of ready resources for the provision of
                      the Services by Supplier, Customer shall pay the Fees upon invoice including where Services cannot
                      be delivered due to Customer&rsquo;s failure to meet any of its obligations under the Services
                      Agreement.
                    </li>
                    <li>Supplier will invoice Customer in accordance with the Order Form or, where not specified in the
                      Order Form, immediately following the Commencement Date of the Order Form on 30-day payment terms.
                    </li>
                    <li>All Services shall be used and, in any event, paid for in full as set forth in this Clause 8 or
                      in the Order Form. Any Services which are unused by Customer during the Initial Term or a relevant
                      Extension Term will expire and shall not be credited, or refunded unless otherwise expressly
                      agreed by the parties in writing.
                    </li>
                    <li>
                      <p>The Fees exclude the following, which shall be payable by Customer monthly in arrears (provided
                        that Supplier has obtained the written consent of Customer, which shall not be unreasonably
                        delayed or withheld), as incurred:</p>
                      <ol type="a">
                        <li>the cost of hotel, subsistence, travelling and any other ancillary expenses reasonably
                          incurred by the individuals whom Supplier engages in connection with the Services; and
                        </li>
                        <li>the cost to Supplier of any materials or services procured from time to time by Supplier, as
                          it deems appropriate, from third parties for the provision of any Service where such items and
                          their cost are approved by Customer in advance, and for any materials or services reasonably
                          deemed necessary to procure by Supplier, in its absolute discretion, where such items and
                          their costs are notified to Customer in advance.
                        </li>
                      </ol>
                    </li>
                    <li>
                      <p>The Fees also exclude services related to non-Supplier delay, cancellation and rescheduling
                        charges, for costs related directly to the administration, system, personnel, facilities, third
                        party and/or other allocated resources associated with scheduled Services. The following charges
                        will apply to any Customer short-term cancellation and rescheduling:</p>
                      <ol type="a">
                        <li>cancellation or rescheduling requested between 7 and 14 days before the scheduled start date
                          for delivery of any Services: 50% of the scheduled Service Fees of the cancelled or
                          rescheduled Service(s); or
                        </li>
                        <li>for cancellation or rescheduling requested within 7 days before the scheduled start date for
                          delivery of any Services: 100% of the scheduled Service Fees of the cancelled or rescheduled
                          Service(s).
                        </li>
                      </ol>
                    </li>
                    <li>Supplier may choose to increase the Fees on an annual basis with effect from each anniversary of
                      the date of the Services Agreement, to cover, e.g., any increased Supplier costs, in line with the
                      higher of five percent (5%) or the percentage increase in the Consumer Price Index in the
                      preceding 12-month period, and the first such increase shall take effect, at Supplier&rsquo;s
                      discretion, on the first anniversary of the date of the Services Agreement and shall be based on
                      the latest available annual figure for the percentage increase in the Consumer Prices Index.
                    </li>
                    <li>Supplier may, at any time during the Initial Term and during any Extension Term thereafter, vary
                      the Fees payable by Customer by giving at least 30 days prior written notice in the event of new
                      taxation laws, or the introduction or increase in any taxes, levies, costs or expenses, including
                      any taxes, levies which relate to the Services;
                    </li>
                    <li>Supplier will invoice Customer for the Fees as set forth in the Order Form or as set forth in
                      the Services Agreement or as otherwise expressly agreed in writing.
                    </li>
                    <li>
                      <p>Customer shall pay each invoice submitted to it by Supplier based on the following terms:</p>
                      <ol type="a">
                        <li>on 30-day terms where indicated by Supplier or any other terms as set forth on the Order
                          Form;
                        </li>
                        <li>by credit card on immediate receipt of the invoice;</li>
                        <li>by direct debit with payments taken 14 days after date of invoice, where credit terms are
                          agreed; or
                        </li>
                        <li>by payment in advance at any time required by Supplier, where Customer&rsquo;s credit score
                          is insufficient to meet the total value of the contract.
                        </li>
                      </ol>
                    </li>
                    <li>
                      <p>Without prejudice to any other right or remedy that it may have, if Customer fails to pay
                        Supplier any sum due under the Services Agreement on the due date:</p>
                      <ol type="a">
                        <li>All sums payable under the Services Agreement for services delivered and to be delivered
                          shall become due and payable by Customer.
                        </li>
                        <li>Customer shall pay interest on the overdue sum from the due date until payment of the
                          overdue sum, whether before or after a court judgment. Interest under this clause will accrue
                          each day at 4% a year above the Bank of England base rate from time to time, but at 4% a year
                          for any period when that base rate is below 0%; and
                        </li>
                        <li>Supplier may suspend or cancel part or all the Services if payment is not received within 10
                          days of the due date until payment has been made in full (subject to any other rights and/or
                          remedies under the Services Agreement).
                        </li>
                      </ol>
                    </li>
                    <li>
                      <p>All amounts payable to Supplier under the Services Agreement:</p>
                      <ol type="a">
                        <li>are exclusive of any applicable VAT, and Customer shall in addition pay an amount equal to
                          any applicable VAT on those sums on receipt of a VAT invoice; and
                        </li>
                        <li>shall be paid in full without any set-off, counterclaim, deduction or withholding (other
                          than any deduction or withholding of tax as required by law) and are excluded from Force
                          Majeure clause 15.
                        </li>
                      </ol>
                    </li>
                  </ol>
                </li>
                <li id="intellectual_property_rights">
                  <h4 className="lh-base lead fw-bold mb-2 text-uppercase">Intellectual property rights</h4>
                  <ol className="decimal-list">
                    <li>
                      <p>In relation to the Services and any Deliverables: </p>
                      <ol type="a">
                        <li>Supplier and its licensors shall retain ownership of all IPRs in the Services and the
                          Deliverables, excluding Customer Materials;
                        </li>
                        <li>Supplier grants Customer, or shall procure the direct grant to Customer of, a fully paid,
                          worldwide, non-exclusive, royalty-free revocable licence during the term of the Services
                          Agreement to copy and modify the Deliverables for the purpose of receiving and using the
                          Services and the Deliverables in its business; and
                        </li>
                        <li>Customer shall not sub-licence, assign or otherwise transfer the rights granted in clause
                          9.1(b) to any of its customers or other third parties, unless expressly agreed in writing with
                          Supplier.
                        </li>
                      </ol>
                    </li>
                    <li>
                      <p>In relation to Customer Materials, Customer:</p>
                      <ol type="a">
                        <li>and its licensors shall retain ownership of all IPRs in Customer Materials; and</li>
                        <li>grants to Supplier a fully paid, non-exclusive, royalty-free, non-transferable licence to
                          copy and modify Customer Materials for the term of the Services Agreement, and as required by
                          law thereafter, for the purpose of providing the Services to Customer.
                        </li>
                      </ol>
                    </li>
                    <li>
                      <p>Supplier:</p>
                      <ol type="a">
                        <li>warrants that the receipt, use of the Services and the Deliverables by Customer shall not
                          infringe the IPRs of any third party;
                        </li>
                        <li>shall, subject to Clause 12 (Limitation of Liability), indemnify Customer against all
                          liabilities, costs, expenses, damages and losses suffered or incurred or paid by Customer
                          arising out of or in connection with any claim brought against Customer for actual or alleged
                          infringement of a third party&rsquo;s Intellectual Property Rights arising out of, or in
                          connection with, the receipt or use of the Services and Deliverables;
                        </li>
                        <li>shall not be in breach of the warranty at clause 9.3(a), and Customer shall have no claim
                          under the indemnity at clause 9.3(b), to the extent the infringement arises from:
                          <ol className="i-list">
                            <li>compliance with Customer&lsquo;s specifications or instructions, where infringement could not
                              have been avoided while complying with such specifications or instructions and provided
                              that Supplier shall notify Customer if it knows or suspects that compliance with such
                              specification or instruction may result in infringement.
                            </li>
                            <li>the use of Customer Materials in the development of, or the inclusion of Customer
                              Materials in, the Services or any Deliverable;
                            </li>
                            <li>any modification of the Services or any Deliverable, other than by or on behalf of
                              Supplier as authorised by Supplier; and
                            </li>
                          </ol>
                        </li>
                      </ol>
                    </li>
                    <li>
                      <p>Customer:</p>
                      <ol type="a">
                        <li>warrants that the receipt and use in the performance of the Services Agreement by Supplier,
                          its agents, employees, subcontractors or consultants of Customer Materials shall not infringe
                          the rights, including any Intellectual Property Rights, of any third party; and
                        </li>
                        <li>shall indemnify Supplier against all liabilities, costs, expenses, damages and losses
                          suffered or incurred or paid by Supplier arising out of or in connection with any claim
                          brought against Supplier, its agents, employees, subcontractors or consultants for actual or
                          alleged infringement of a third party&lsquo;s Intellectual Property Rights, to the extent that the
                          infringement or alleged infringement arises out of, or in connection with, the receipt or use
                          of Customer Materials in the performance of the Services Agreement.
                        </li>
                      </ol>
                    </li>
                    <li>
                      <p>If either party (<strong>Indemnifying Party</strong>) is required to indemnify the other party
                        (<strong>Indemnified Party</strong>) under this clause 9, the Indemnified Party shall:</p>
                      <ol type="a">
                        <li>notify the Indemnifying Party in writing of any claim against it in respect of which it
                          wishes to rely on the indemnity at clause 9.3(b) or clause 9.4(b) (as applicable) (<strong>IPRs
                            Claim</strong>);
                        </li>
                        <li>allow the Indemnifying Party, at its own cost, to conduct all negotiations and proceedings
                          and to settle the IPRs Claim, provided that the Indemnifying Party shall obtain the
                          Indemnified Party&lsquo;s prior approval of any settlement agreement, such not to be unreasonably
                          withheld, delayed, or conditioned;
                        </li>
                        <li>provide the Indemnifying Party with such reasonable assistance regarding the IPRs Claim as
                          is required by the Indemnifying Party, subject to reimbursement by the Indemnifying Party of
                          the Indemnified Party&lsquo;s costs so incurred; and
                        </li>
                        <li>not, without prior consultation with the Indemnifying Party, make any admission relating to
                          the IPRs Claim or attempt to settle it, provided that the Indemnifying Party considers and
                          defends any IPRs Claim diligently, using counsel and in such a way as not to bring the
                          Indemnified Party&rsquo;s reputation into disrepute.
                        </li>
                      </ol>
                    </li>
                    <li><a href="https://www.lawinsider.com/clause/publicity-and-use-of-trademarks" target="_blank"
                           rel="noreferrer noopener">https://www.lawinsider.com/clause/publicity-and-use-of-trademarks</a> Neither
                      Party shall use any Intellectual Property Rights except with express prior written consent, which
                      consent shall not be unreasonably withheld.
                    </li>
                  </ol>
                </li>
                <li id="data_protection">
                  <h4 className="lh-base lead fw-bold mb-2 text-uppercase">Data protection </h4>
                  <ol className="decimal-list">
                    <li>For the purposes of this clause 10, <strong>controller, processor, data subject, personal data,
                      personal data breach</strong> and <strong>processing</strong> shall have the meaning given to them
                      in the UK GDPR.
                    </li>
                    <li>Both parties will comply with all applicable requirements of Applicable Data Protection Laws.
                      This clause 10 is in addition to, and does not relieve, remove or replace, a party&lsquo;s obligations
                      or rights under Applicable Data Protection Laws.
                    </li>
                    <li>Customer consents to, (and shall procure all required consents, from its personnel,
                      representatives and agents, in respect of) all actions taken by Supplier in connection with the
                      processing of Customer Personal Data, provided these are in compliance with the then-current
                      version of Supplier&lsquo;s privacy policy available at <a
                        href="https://www.bulletproof.co.uk/privacy-notice" target="_blank"
                        rel="noreferrer noopener">https://www.bulletproof.co.uk/privacy-notice</a> (<strong>Privacy
                        Policy</strong>). In the event of any inconsistency or conflict between the Privacy Policy and
                      the Services Agreement, the Privacy Policy will take precedence.
                    </li>
                    <li>Customer will ensure that it has all necessary consents and notices in place to enable lawful
                      transfer of Customer Personal Data to Supplier for the duration and purposes of the Services
                      Agreement.
                    </li>
                    <li>
                      <p>Without prejudice to the generality of clause 10.2, Supplier shall, in relation to Customer
                        Personal Data:</p>
                      <ol type="a">
                        <li>process that Customer Personal Data only on the documented instructions of Customer unless
                          Supplier is required by Applicable Laws to otherwise process that Customer Personal Data
                          (<strong>Purpose</strong>). Where Supplier is relying on Applicable Laws as the basis for
                          processing Customer Personal Data, Supplier shall notify Customer of this before performing
                          the processing required by the Applicable Laws unless those Applicable Laws prohibit the
                          Supplier from so notifying Customer on important grounds of public interest. Supplier shall
                          inform Customer if, in the opinion of Supplier, the instructions of Customer infringe
                          Applicable Data Protection Laws;
                        </li>
                        <li>implement technical and organisational measures to protect against unauthorised or unlawful
                          processing of Customer Personal Data and against accidental loss or destruction of, or damage
                          to, Customer Personal Data, which Customer has reviewed and confirms are appropriate to the
                          harm that might result from the unauthorised or unlawful processing or accidental loss,
                          destruction or damage and the nature of the data to be protected, having regard to the state
                          of technological development and the cost of implementing any measures;
                        </li>
                        <li>ensure that any personnel engaged and authorised by Supplier to process Customer Personal
                          Data have committed themselves to confidentiality or are under an appropriate statutory or
                          common law obligation of confidentiality;
                        </li>
                        <li>assist Customer insofar as this is possible (taking into account the nature of the
                          processing and the information available to Supplier), and at Customer&lsquo;s cost and written
                          request, in responding to any request from a data subject and in ensuring Customer&lsquo;s
                          compliance with its obligations under Applicable Data Protection Laws with respect to
                          security, breach notifications, impact assessments and consultations with supervisory
                          authorities or regulators;
                        </li>
                        <li>notify Customer without undue delay on becoming aware of a personal data breach involving
                          Customer Personal Data; Where such breach is notifiable to the Information
                          Commissioner&rsquo;s Office (ICO), Supplier shall notify the ICO or other relevant supervisory
                          authority of such breach at the end of any statutorily required notice period where the
                          requisite notice has not been sent earlier either by Customer or Supplier at Customer&rsquo;s
                          instruction;
                        </li>
                        <li>at the written direction of Customer, delete or return Customer Personal Data and copies
                          thereof to Customer on termination of the Services Agreement unless Supplier is required by
                          Applicable Law to continue to process that Customer Personal Data. For the purposes of this
                          clause 10.5(f) Customer Personal Data shall be considered deleted where it is put beyond
                          further use by Supplier;
                        </li>
                        <li>will only process personal data in an identifiable form for no longer than is necessary for
                          the purposes for which it is processed, including but not limited to complying with its
                          obligations under the Payment Card Industry Data Security Standard (PCI DSS) rules which
                          prohibits the storage of payment card verification codes once a transaction has been
                          authorised; and
                        </li>
                        <li>maintain records to demonstrate its compliance with this clause 10, and allow for reasonable
                          audits by Customer or Customer&lsquo;s designated auditor, for this purpose, on reasonable written
                          notice to a maximum of once annually.
                        </li>
                      </ol>
                    </li>
                    <li>
                      <p>Customer provides its prior, general authorisation for Supplier to:</p>
                      <ol type="a">
                        <li>
                          <p>appoint processors to process Customer Personal Data, provided that Supplier:</p>
                          <ol type="i">
                            <li>shall ensure that the Services Agreement on which it appoints such processors comply
                              with Applicable Data Protection Laws, and are consistent with the obligations imposed on
                              Supplier in this clause 10;
                            </li>
                            <li>shall remain responsible for the acts and omission of any such processor as if they were
                              the acts and omissions of Supplier; and
                            </li>
                          </ol>
                        </li>
                        <li>transfer Customer Personal Data outside of the UK as required for the Purpose, provided that
                          Supplier shall ensure that all such transfers are made in accordance with Applicable Data
                          Protection Laws. For these purposes, Customer shall promptly comply with any reasonable
                          request of Supplier, including any request to enter into standard data protection clauses
                          adopted by the EU Commission from time to time (where the EU GDPR applies to the transfer) or
                          adopted by the ICO from time to time (where the UK GDPR applies to the transfer).
                        </li>
                      </ol>
                    </li>
                    <li>Either party may, at any time on not less than 30 days&lsquo; written notice, revise this clause 10 by
                      replacing it with any applicable controller to processor standard clauses or similar agreement
                      forming part of an applicable certification scheme (which shall apply when replaced by Annex to
                      the Services Agreement).
                    </li>
                    <li>Supplier&lsquo;s liability for losses arising from breaches of this clause 10 is as set out in Clause
                      12 (Limitation of Liability).
                    </li>
                  </ol>
                </li>
                <li id="confidentiality">
                  <h4 className="lh-base lead fw-bold mb-2 text-uppercase">Confidentiality </h4>
                  <ol className="decimal-list">
                    <li>Each party undertakes that it shall not at any time use or disclose to any person any
                      Confidential Information of the other party or of any member of the group of companies to which
                      the other party belongs, except as permitted by clause 11.2.
                    </li>
                    <li>
                      <p>Each party may disclose Confidential Information: </p>
                      <ol type="a">
                        <li>to its employees, officers, representatives, contractors, subcontractors or advisers who
                          need to know such information for the purposes of exercising the party&lsquo;s rights or carrying
                          out its obligations under or in connection with the Services Agreement. Each party shall
                          ensure that its employees, officers, representatives, contractors, subcontractors or advisers
                          to whom it discloses the other party&lsquo;s confidential information comply with this clause 11;
                          and
                        </li>
                        <li>as may be required by law, a court of competent jurisdiction or any governmental or
                          regulatory authority.
                        </li>
                      </ol>
                    </li>
                    <li>No party shall use the other party&lsquo;s confidential information for any purpose other than to
                      exercise its rights and perform its obligations under or in connection with the Services
                      Agreement.
                    </li>
                  </ol>
                </li>
                <li id="limitation_of_liability">
                  <h4 className="lh-base lead fw-bold mb-2 text-uppercase">Limitation of liability </h4>
                  <ol className="decimal-list">
                    <li><strong>Scope of this clause.</strong> References to liability in this clause 12 (Limitation of
                      liability) are subject always to clause 12.3 (liabilities which cannot legally be limited), but
                      otherwise include every kind of liability arising under or in connection with the Services
                      Agreement including but not limited to liability in contract, tort (including negligence),
                      misrepresentation, restitution or otherwise.
                    </li>
                    <li><strong>No limitation of Customer&lsquo;s payment obligations.</strong> Nothing in this clause 12
                      shall limit Customer&lsquo;s payment obligations under the Services Agreement.
                    </li>
                    <li>
                      <p><strong>Liabilities which cannot legally be limited.</strong> Nothing in the Services Agreement
                        limits any liability which cannot legally be limited, including but not limited to liability
                        for:</p>
                      <ol type="a">
                        <li>death or personal injury caused by negligence;</li>
                        <li>fraud or fraudulent misrepresentation; or</li>
                        <li>breach of the Services Agreement by a proven breach of applicable criminal law.</li>
                      </ol>
                    </li>
                    <li><strong>Cap on liability.</strong> SUBJECT TO CLAUSE 12.3 (LIABILITIES WHICH CANNOT LEGALLY BE
                      LIMITED), AND TO CLAUSE 12.6, THE LIABILITY OF EACH OF THE PARTIES SHALL NOT EXCEED THE FEES PAID
                      IN THE 12 MONTH PERIOD PRECEDING THE CLAIM OR, WHERE LESS THAN 12 MONTHS HAVE PASSED, THE
                      EQUIVALENT OF 12 MONTHS&rsquo; WORTH OF FEES, PER CLAIM AND IN AGGREGATE.
                    </li>
                    <li>
                      <p><strong>Specific heads of excluded loss.</strong> SUBJECT TO CLAUSE 12.2 (NO LIMITATION OF
                        CUSTOMER&lsquo;S PAYMENT OBLIGATIONS), CLAUSE 12.3 (LIABILITIES WHICH CANNOT LEGALLY BE LIMITED), THIS
                        CLAUSE 12.5 SPECIFIES THE TYPES OF LOSSES THAT ARE EXCLUDED:</p>
                      <ol type="a">
                        <li>LOSS OF PROFITS;</li>
                        <li>LOSS OF REVENUES, LOSS OF GOODWILL;</li>
                        <li>LOSS OF AGREEMENTS, LOSS OF BUSINESS OPPORTUNITY;</li>
                        <li>LOSS OF BUSINESS;</li>
                        <li>DEPLETION OF GOODWILL OR SIMILAR LOSSES;</li>
                        <li>PURE ECONOMIC LOSS; AND</li>
                        <li>FOR ANY INDIRECT OR CONSEQUENTIAL LOSS, COSTS, DAMAGES, CHARGES OR EXPENSES HOWEVER
                          ARISING.
                        </li>
                      </ol>
                    </li>
                    <li>EACH PARTY’S TOTAL LIABILITY TO THE OTHER FOR LOSSES FOR BREACHES OF CLAUSE 9 (INTELLECTUAL
                      PROPERTY RIGHTS), CLAUSE 10 (DATA PROTECTION), CLAUSE 5 (CUSTOMER INDEMNITY) AND CLAUSE 11
                      (CONFIDENTIALITY), SHALL BE LIMITED TO AND SHALL NOT EXCEED GB£3,000,000.
                    </li>
                    <li>Customer acknowledges that there is a risk that a Service may lead to the loss or corruption of
                      Customer&rsquo;s data affected by the Services, and that the same is an inherent risk of receiving
                      a Service even when performed in accordance with Good Industry Practice. Customer agrees to back
                      up its data prior to delivery of any Service set forth in the Order Form. Except where otherwise
                      provided herein, Supplier will not be liable for any such loss of data.
                    </li>
                    <li>Supplier disclaims and excludes any and all warranties, terms or conditions (not expressly
                      stated in the Services Agreement) as permitted by law, including implied warranties, terms or
                      conditions relating to the acceptable quality and fitness for purpose. Customer is solely
                      responsible for the suitability of the Services chosen.
                    </li>
                    <li>Customer warrants that it has the full capacity and authority to instruct Supplier to deliver
                      the Services and will not hold Supplier liable for any violation of the Computer Misuse Act 1990
                      or any other local applicable laws, rules or regulations.
                    </li>
                    <li>Except as expressly provided for in the Services Agreement, Customer hereby acknowledges that
                      Services set forth in the Order Form are delivered on an as is basis and Supplier shall only be
                      liable to the extent set forth in the Services Agreement.
                    </li>
                  </ol>
                </li>
                <li id="termination">
                  <h4 className="lh-base lead fw-bold mb-2 text-uppercase">Termination</h4>
                  <ol className="decimal-list">
                    <li>
                      <p>Either party may immediately terminate the Services Agreement without payment of compensation
                        or other damages caused to the other solely by such termination by giving notice to the other if
                        any one or more of the following occurs:</p>
                      <ol type="a">
                        <li>the other party commits a material breach of any term of the Services Agreement and such
                          breach is irremediable or (if such breach is remediable) fails to remedy that breach within 30
                          days after being notified in writing to do so;
                        </li>
                        <li>the other party suspends, or threatens to suspend, payment of its debts or is unable to pay
                          its debts as they fall due or admits inability to pay its debts or (being a company or limited
                          liability partnership) is deemed unable to pay its debts within the meaning of section 123 of
                          the Insolvency Act 1986, or (being an individual) is deemed either unable to pay its debts or
                          as having no reasonable prospect of so doing, in either case, within the meaning of section
                          268 of the Insolvency Act 1986, or (being a partnership) has any partner to whom any of the
                          foregoing apply;
                        </li>
                        <li>the other party commences negotiations with all or any class of its creditors with a view to
                          rescheduling any of its debts, or makes a proposal for or enters into any compromise or
                          arrangement with any of its creditors other than for the sole purpose of a scheme for a
                          solvent amalgamation of that other party with one or more other companies or the solvent
                          reconstruction of that other party;
                        </li>
                        <li>a petition is filed, a notice is given, a resolution is passed, or an order is made, for or
                          in connection with the winding up of that other party (being a company) other than for the
                          sole purpose of a scheme for a solvent amalgamation of that other party with one or more other
                          companies or the solvent reconstruction of that other party;
                        </li>
                        <li>an application is made to court, or an order is made, for the appointment of an
                          administrator, or if a notice of intention to appoint an administrator is given or if an
                          administrator is appointed, over the other party (being a company);
                        </li>
                        <li>the holder of a qualifying floating charge over the assets of that other party (being a
                          company) has become entitled to appoint or has appointed an administrative receiver;
                        </li>
                        <li>a person becomes entitled to appoint a receiver over all or any of the assets of the other
                          party or a receiver is appointed over all or any of the assets of the other party;
                        </li>
                        <li>a creditor or encumbrancer of the other party attaches or takes possession of, or a
                          distress, execution, sequestration or other such process is levied or enforced on or sued
                          against, the whole or any part of the other party&lsquo;s assets and such attachment or process is
                          not discharged within 14 days;
                        </li>
                        <li>any event occurs, or proceeding is taken, with respect to the other party in any
                          jurisdiction to which it is subject that has an effect equivalent or similar to any of the
                          events mentioned in clause 13.1(b) to clause 13.1(h) (inclusive); or
                        </li>
                        <li>the other party suspends or ceases, or threatens to suspend or cease, carrying on all or a
                          substantial part of its business.
                        </li>
                      </ol>
                    </li>
                    <li>For the purposes of clause 13.1(a) material breach means a breach (including an anticipatory
                      breach) that is serious in the widest sense of having a serious effect on the benefit which the
                      terminating party would otherwise derive from a substantial portion of the Services Agreement.
                    </li>
                    <li>Without affecting any other right or remedy available to it, including payment by Customer of
                      all fees due under the Services Agreement, Supplier may terminate the Services Agreement with
                      immediate effect by giving written notice to Customer if Customer is in material breach of any
                      other Supplier Order Form/Services Agreement or fails to pay any amount due under any Services
                      Agreement on the due date for payment and remains in default more than 30 days after being
                      notified to make such payment.
                    </li>
                    <li>Customer may send express written notice of its intention to terminate the Services Agreement
                      within 30 days of the date it receives 90 days express written notice from Supplier of any
                      material update to the Standard Terms (under clause 17) where that updated term cannot by law or
                      policy, applicable at the time, be accepted by Customer. All fees otherwise due and payable under
                      the Services Agreement must be paid in accordance with the Services Agreement including, without
                      limitation, all fees for any delivered services.
                    </li>
                  </ol>
                </li>
                <li id="consequences_of_termination_and_survival">
                  <h4 className="lh-base lead fw-bold mb-2 text-uppercase">Consequences of termination and survival</h4>
                  <ol className="decimal-list">
                    <li>Consequences of termination or expiry. Except as otherwise provided, the termination or expiry
                      of the Services Agreement shall terminate all licences, access and other rights to the Services
                      and/or Defense.com Platform and Customer shall deliver any Supplier Equipment in its possession to
                      Supplier and destroy all copies of Supplier Confidential Information. Except as otherwise
                      provided, Supplier shall destroy any copies of Customer confidential information. Customer shall
                      immediately pay to Supplier all of Supplier&lsquo;s outstanding unpaid invoices, invoices to be
                      submitted for Services supplied to the date of termination and related interest and, except where
                      Customer has rightfully terminated for Supplier&rsquo;s material breach, Supplier may submit an
                      invoice payable upon receipt in respect of the Services to be supplied but for which no invoice
                      has been submitted.
                    </li>
                    <li>Survival. On termination, where Customer has terminated for Supplier&rsquo;s material breach, or
                      expiry of the Services Agreement, any existing Order Form shall continue until the Services have
                      been completed or, before completion, at Customer&rsquo;s reasonable request. Any provision of the
                      Services Agreement that expressly or by implication is intended to come into or continue in force
                      on or after termination or expiry of the Services Agreement shall remain in full force and effect.
                      Termination or expiry of the Services Agreement shall not affect any rights, remedies, obligations
                      or liabilities of the parties that have accrued up to the date of termination or expiry, including
                      the right to claim damages in respect of any breach of the Services Agreement which existed at the
                      date of termination or expiry.
                    </li>
                  </ol>
                </li>
                <li id="force_majeure">
                  <h4 className="lh-base lead fw-bold mb-2 text-uppercase">Force majeure </h4>
                  <ol className="decimal-list">
                    <li>
                      <p><strong>Force Majeure Event</strong> means any circumstance, except for Customer&rsquo;s
                        payment obligations, not within a party&lsquo;s reasonable control including, without limitation:</p>
                      <ol type="a">
                        <li>acts of God, flood, drought, earthquake or other natural disaster;</li>
                        <li>epidemic or pandemic or Government mandated lockdowns or other related restrictions;</li>
                        <li>terrorist or cyber-attack, civil war, civil commotion or riots, war, threat of or
                          preparation for war, armed conflict, imposition of sanctions, embargo, or breaking off of
                          diplomatic relations;
                        </li>
                        <li>nuclear, chemical or biological contamination or sonic boom;</li>
                        <li>any law or any action taken by a government or public authority, including without
                          limitation imposing an export or import restriction, quota or prohibition, or failing to grant
                          a necessary licence or consent;
                        </li>
                        <li>collapse of buildings, fire, explosion or accident;</li>
                        <li>any labour or trade dispute, strikes, industrial action or lockouts (other than in each case
                          by the party seeking to rely on this clause, or companies in the same group as that party);
                        </li>
                        <li>non-performance by suppliers or subcontractors (other than by companies in the same group as
                          the party seeking to rely on this clause); and
                        </li>
                        <li>interruption or failure of a utility service.</li>
                      </ol>
                    </li>
                    <li>Provided it has complied with clause 15.4, if a party is prevented, hindered or delayed in or
                      from performing any of its obligations under the Services Agreement by a Force Majeure Event
                      (<strong>Affected Party</strong>), the Affected Party shall not be in breach of the Services
                      Agreement or otherwise liable for any such failure or delay in the performance of such
                      obligations.
                    </li>
                    <li>The corresponding obligations of the other party will be suspended, and its time for performance
                      of such obligations extended, to the same extent as those of the Affected Party.
                    </li>
                    <li>
                      <p>The Affected Party shall:</p>
                      <ol type="a">
                        <li>as soon as reasonably practicable after the start of the Force Majeure Event but no later
                          than ten days from its start, notify the other party of the Force Majeure Event, the date on
                          which it started, its likely or potential duration, and the effect of the Force Majeure Event
                          on its ability to perform any of its obligations under the Services Agreement; and
                        </li>
                        <li>use all reasonable endeavours to mitigate the effect of the Force Majeure Event on the
                          performance of its obligations.
                        </li>
                      </ol>
                    </li>
                    <li>If the Force Majeure Event prevents, hinders or delays the Affected Party&lsquo;s performance of its
                      obligations for a continuous period of more than six weeks, the party not affected by the Force
                      Majeure Event may terminate the Services Agreement by giving 21 days&rsquo; written notice to the
                      Affected Party.
                    </li>
                  </ol>
                </li>
                <li id="assignment_and_other_dealings">
                  <h4 className="lh-base lead fw-bold mb-2 text-uppercase">Assignment and other dealings</h4>
                  <ol className="decimal-list">
                    <li>Customer shall not assign, transfer, mortgage, charge, subcontract, delegate, declare a trust
                      over or deal in any other manner with any of its rights and obligations under the Services
                      Agreement, without prior express written consent from Supplier, such consent not to be
                      unreasonably withheld.
                    </li>
                    <li>Supplier may mortgage, charge, delegate, assign, novate or otherwise transfer any or all its
                      rights under the Services Agreement. Supplier shall not novate or assign its rights and
                      obligations under the Services Agreement to another service party without prior notice to
                      Customer.
                    </li>
                  </ol>
                </li>
                <li id="amendment">
                  <h4 className="lh-base lead fw-bold mb-2 text-uppercase">Amendment</h4>
                  <p>No amendment or variation of the Services Agreement shall be effective without express written
                    consent signed by the parties (or their authorised representatives) except that Supplier may from
                    time to time update the Services Agreement Standard Terms or Services Agreement Service-specific
                    Terms upon 90 days express written notice to Customer upon which Customer may send express written
                    notice of its intent to terminate the Services Agreement as provided for in clause 13.4.</p>
                </li>
                <li id="waiver">
                  <h4 className="lh-base lead fw-bold mb-2 text-uppercase">Waiver</h4>
                  <ol className="decimal-list">
                    <li>A waiver of any right or remedy under the Services Agreement or by law is only effective if
                      given expressly in writing and shall not be deemed a waiver of any subsequent right or remedy.
                    </li>
                    <li>A failure or delay by a party to exercise any right or remedy provided under the Services
                      Agreement or by law shall not constitute a waiver of that or any other right or remedy, nor shall
                      it prevent or restrict any further exercise of that or any other right or remedy. No single or
                      partial exercise of any right or remedy provided under the Services Agreement or by law shall
                      prevent or restrict the further exercise of that or any other right or remedy.
                    </li>
                  </ol>
                </li>
                <li id="rights_and_remedies">
                  <h4 className="lh-base lead fw-bold mb-2 text-uppercase">Rights and remedies</h4>
                  <p>The rights and remedies provided under the Services Agreement are in addition to, and not exclusive
                    of, any rights or remedies provided by law. </p>
                </li>
                <li id="severance">
                  <h4 className="lh-base lead fw-bold mb-2 text-uppercase">Severance</h4>
                  <ol className="decimal-list">
                    <li>If any provision or part-provision of the Services Agreement is or becomes invalid, illegal or
                      unenforceable, it shall be deemed deleted, but that shall not affect the validity and
                      enforceability of the rest of the Services Agreement.
                    </li>
                    <li>If any provision or part-provision of the Services Agreement is deemed deleted under clause 20.1
                      the parties shall negotiate in good faith to agree a replacement provision that, to the greatest
                      extent possible, achieves the intended commercial result of the original provision.
                    </li>
                  </ol>
                </li>
                <li id="entire_agreement">
                  <h4 className="lh-base lead fw-bold mb-2 text-uppercase">Entire Agreement</h4>
                  <ol className="decimal-list">
                    <li>The Services Agreement constitutes the entire agreement between the parties and supersedes and
                      extinguishes all previous agreements, contracts, promises, assurances, warranties, representations
                      and understandings between them, whether written, oral or by conduct, relating to its subject
                      matter.
                    </li>
                    <li>Each party agrees it shall have no remedies in respect of any statement, representation,
                      assurance or warranty (whether made innocently or negligently) that is not set out in the Services
                      Agreement. Each party agrees it shall have no claim for innocent or negligent misrepresentation or
                      negligent misstatement based on any statement in the Services Agreement.
                    </li>
                  </ol>
                </li>
                <li id="no_partnership_or_agency">
                  <h4 className="lh-base lead fw-bold mb-2 text-uppercase">No partnership or agency</h4>
                  <ol className="decimal-list">
                    <li>Nothing in the Services Agreement is intended or shall be deemed to establish a partnership or
                      joint venture between any of the parties, constitute any party the agent of another party, or
                      authorise any party to make or enter into any commitments for or on behalf of any other party.
                    </li>
                    <li>Each party confirms it is acting on its own behalf and not for the benefit of any other
                      person.
                    </li>
                  </ol>
                </li>
                <li id="anti_bribery_and_anti_corruption">
                  <h4 className="lh-base lead fw-bold mb-2 text-uppercase">Anti-Bribery and Anti-Corruption</h4>
                  <p>Each Party shall, and shall ensure any of its agents, employees, consultants, contractors and
                    subcontractors shall, comply with all applicable laws, statutes, regulation, and codes relating to
                    anti-bribery and anti-corruption including but not limited to the Bribery Act 2010 and shall
                    establish, maintain and enforce its own policies and procedures to ensure compliance. </p>
                </li>
                <li id="anti_slavery_and_human_trafficking">
                  <h4 className="lh-base lead fw-bold mb-2 text-uppercase">Anti-Slavery and Human Trafficking</h4>
                  <p>Each Party shall, in performing its obligations under the Services Agreement, comply with all
                    applicable anti-slavery and human trafficking laws, statutes and regulations from time to time in
                    force including the Modern Slavery Act 2015; and each party represents and warrants that it has not
                    been convicted of any offence involving slavery and human trafficking or been the subject of any
                    investigation, inquiry or enforcement proceedings regarding any offence or alleged offence of or in
                    connection with such trafficking. </p>
                </li>
                <li id="third_party_rights">
                  <h4 className="lh-base lead fw-bold mb-2 text-uppercase">Third party rights</h4>
                  <p>Except as otherwise agreed, the Services Agreement does not give rise to any third-party statutory
                    rights to enforce any of its terms. </p>
                </li>
                <li id="notices">
                  <h4 className="lh-base lead fw-bold mb-2 text-uppercase">Notices</h4>
                  <ol className="decimal-list">
                    <li>Any notice given to a party under or in connection with the Services Agreement shall be in
                      writing and shall be delivered by e-mail, by hand or by tracked post or courier service at the
                      recipient party&rsquo;s registered office (if a company) or its principal place of business (in
                      any other case).
                    </li>
                    <li>Any notice shall be deemed to have been received:
                      <ol type="a">
                        <li>if by e-mail, at the time of transmission (assuming no failure notification or other
                          indication of non-delivery is received);
                        </li>
                        <li>if delivered by hand, at the time the notice is left at the proper address; or</li>
                        <li>if sent tracked and signed-for delivery by national courier, at the time such courier
                          confirms delivery.
                        </li>
                      </ol>
                    </li>
                    <li>This clause does not apply to the service of any proceedings or any documents in any legal
                      action or, where applicable, any arbitration or other formal method of dispute resolution.
                    </li>
                    <li>A notice given under the Services Agreement is valid if received.</li>
                  </ol>
                </li>
                <li id="governing_law_venue_and_dispute_resolution">
                  <h4 className="lh-base lead fw-bold mb-2 text-uppercase">Governing law, venue and dispute
                    resolution</h4>
                  <ol className="decimal-list">
                    <li>The Services Agreement shall be governed and construed in accordance with English law.</li>
                    <li>Any dispute arising under or related to the Services Agreement that is not resolved by good
                      faith discussion among the parties, at their discretion, shall be resolved by binding fast-track
                      London Court of International Arbitration (LCIA) arbitration in London with the exception of an
                      action brought in any court having jurisdiction to enforce terms of an arbitration award under
                      this clause or for injunctive relief or, for Supplier at its discretion, where the sole or primary
                      dispute regards payment by Customer.
                    </li>
                  </ol>
                </li>
                <li id="counterparts">
                  <h4 className="lh-base lead fw-bold mb-2 text-uppercase">Counterparts </h4>
                  <p>The Services Agreement Order Form may be executed and delivered electronically or by hardcopy in
                    any number of counterparts, each of which shall constitute a duplicate original, but all
                    counterparts together constitute the one Services Agreement Order Form. No counterpart shall be
                    effective until each party has executed at least one counterpart.</p>
                </li>
              </ol>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServicesAgreementStandard;